import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {Config} from 'smag-commons/cfg/connector/bacnet';
import * as _ from 'lodash';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {
    validateInteger,
    validateNotBlank,
    validateNotBlankTrim,
    validateNotDuplicate
} from '../../../commons/validators';
import {BehaviorSubject, merge, Observable, of, Subject, Subscription} from 'rxjs';
import {map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {
    PollingPolicy,
    PollingPolicyComponent,
    PollingPolicyData,
    setDevicePolicy
} from '../polling-policy/polling-policy.component';
import {DEFAULT_POLLING_INTERVAL, EDITOR_BUILTIN_FUNCTIONS, EDITOR_BUILTIN_TYPES} from '../connectors';
import {Features} from '../expression-editor/expression-editor.component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {isBlank, isDefined, keySorter} from '../../../commons/commons';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {I18NService} from '../../../i18n/i18n.service';
import {SmagApiService} from '../../../api/smag-api.service';

interface BacnetDeviceConfig {
    bacnetDevId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface DeviceConfig {
    devId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface PagedBacnetDevices {
    data: BacnetDeviceConfig[];
    total: number;
}

interface PagedDevices {
    data: DeviceConfig[];
    total: number;
}

const VARIABLE_ACCESSORS = ['p', 'v']; // "points", "variable" (command)

const BACNET_DEVICE_ADD_DIALOG_MAX_WIDTH = 500;
const BACNET_DEVICE_RENAME_DIALOG_MAX_WIDTH = 500;
const BACNET_DEVICE_DELETE_DIALOG_MAX_WIDTH = 500;
const DEVICE_ADD_DIALOG_MAX_WIDTH = 500;
const DEVICE_RENAME_DIALOG_MAX_WIDTH = 500;
const DEVICE_DELETE_DIALOG_MAX_WIDTH = 500;
const DEVICE_POLLING_POLICY_DIALOG_MAX_WIDTH = 500;
const DEVICE_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH = 500;
const PAGE_SIZE = 5;
const PAGE_SIZES = [5, 10, 15, 30];

const EXPRESSION_HELP = 'Input points are available through point-value map "p". Command value is supplied in variable "v".__{bacnet}';

@Component({
    selector: 'app-bacnet-config',
    templateUrl: './bacnet-config.component.html',
    styleUrls: ['./bacnet-config.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Items per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class BacnetConfigComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadBacnetDevsPages: Subject<void>;
    private reloadDevsPages: Subject<void>;
    @ViewChild('addBacnetDevice') private addBacnetDeviceDialog: TemplateRef<any>;
    @ViewChild('renameBacnetDevice') private renameBacnetDeviceDialog: TemplateRef<any>;
    @ViewChild('deleteBacnetDevice') private deleteBacnetDeviceDialog: TemplateRef<any>;
    @ViewChild('addDevice') private addDeviceDialog: TemplateRef<any>;
    @ViewChild('renameDevice') private renameDeviceDialog: TemplateRef<any>;
    @ViewChild('deleteDevice') private deleteDeviceDialog: TemplateRef<any>;
    @ViewChild('advancedDeviceSettings') private advancedDeviceSettingsDialog: TemplateRef<any>;

    @ViewChild('bacnetDevsPaginator') private bacnetDevsPaginator: MatPaginator;
    @ViewChild('devsPaginator') private devsPaginator: MatPaginator;

    pagedBacnetDevs: BehaviorSubject<PagedBacnetDevices>;
    pagedDevs: BehaviorSubject<PagedDevices>;

    @Input('id') id: string;
    @Input('config') config: Config;
    @Output('configChange') configEmitter: EventEmitter<Config> = new EventEmitter<Config>();
    @Output('valid') validEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    cfg: Config;
    form: FormGroup;
    bacnetDevices: BacnetDeviceConfig[];
    devices: DeviceConfig[];

    // exported in HTML
    EXPRESSION_HELP = EXPRESSION_HELP;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly appCfg: AppConfig,
                private readonly apiService: SmagApiService,
                private fb: FormBuilder,
                private readonly dialog: MatDialog,
                private ngZone: NgZone,
                private readonly changeDetectorRef: ChangeDetectorRef) {
        this.reloadBacnetDevsPages = new Subject<void>();
        this.reloadDevsPages = new Subject<void>();
        this.pagedBacnetDevs = new BehaviorSubject<PagedBacnetDevices>(null);
        this.pagedDevs = new BehaviorSubject<PagedDevices>(null);
    }

    ngOnInit() {
        // NOTE: config is not supposed to be changed externally outside of component's creation
        this.cfg = _.cloneDeep(this.config);
        this.form = this.fb.group({
            host: new FormControl(this.cfg.host, [validateNotBlank]),
            bbdm: new FormControl(this.cfg.bbdm, []),
            ttl: new FormControl(this.cfg.ttl, [Validators.required, validateInteger, Validators.min(0)])
        });
        this.bacnetDevices = Object.keys(this.cfg.bacnet_devices).map(bacnetDevId => this.mkBacnetDeviceConfig(bacnetDevId));
        this.bacnetDevices.sort(keySorter('bacnetDevId'));
        this.devices = Object.keys(this.cfg.devices).map(devId => this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            this.cfg.host = value['host'];
            this.cfg.bbdm = value['bbdm'] && value['bbdm'].trim().length ? value['bbdm'].trim() : null;
            this.cfg.ttl = value['ttl'];
            this.updateConfig();
        });
        this.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        // do not send an initial config update, as it may trigger a (useless) REST call
        this.updateValidity();
    }

    ngAfterViewInit() {
        merge(this.devsPaginator.page,
            this.reloadDevsPages.pipe(tap(() => this.devsPaginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedDevices(this.devsPaginator.pageSize, this.devsPaginator.pageIndex).pipe(
                    startWith(null as PagedDevices) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedDevs => {
            if (pagedDevs) {
                this.pagedDevs.next(pagedDevs);
                this.changeDetectorRef.detectChanges();
            }
        });

        merge(this.bacnetDevsPaginator.page,
            this.reloadBacnetDevsPages.pipe(tap(() => this.bacnetDevsPaginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedBacnetDevices(this.bacnetDevsPaginator.pageSize, this.bacnetDevsPaginator.pageIndex).pipe(
                    startWith(null as PagedBacnetDevices) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedBacnetDevs => {
            if (pagedBacnetDevs) {
                this.pagedBacnetDevs.next(pagedBacnetDevs);
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.complete();
        this.destroyed.next();
    }

    trackByBacnetDevId(index: number, bacnetDevCfg: BacnetDeviceConfig): string {
        return bacnetDevCfg.bacnetDevId;
    }

    trackByDevId(index: number, devCfg: DeviceConfig): string {
        return devCfg.devId;
    }

    onClickAddBacnetDevice() {
        const bacnetDevs = this.bacnetDevices.map(bacnetDev => bacnetDev.bacnetDevId);
        this.dialog.open(this.addBacnetDeviceDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank, validateNotDuplicate(bacnetDevs)])
            },
            maxWidth: `${BACNET_DEVICE_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addBacnetDevice(id);
            }
        });
    }

    onClickRenameBacnetDevice(bacnetDevId: string) {
        const bacnetDevs = this.bacnetDevices.map(bacnetDev => bacnetDev.bacnetDevId).filter(id => id !== bacnetDevId);
        const connectedDevs = this.devices.filter(dev => this.cfg.devices[dev.devId].bacnet_device === bacnetDevId);
        this.dialog.open(this.renameBacnetDeviceDialog, {
            data: {
                bacnetDevId,
                connectedDevs: connectedDevs.map(dev => dev.devId),
                idFormControl: new FormControl(bacnetDevId, [validateNotBlank, validateNotDuplicate(bacnetDevs)])
            },
            maxWidth: `${BACNET_DEVICE_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== bacnetDevId) {
                this.renameBacnetDevice(bacnetDevId, newName, connectedDevs);
            }
        });
    }

    onClickDeleteBacnetDevice(bacnetDevId: string) {
        const connectedDevs = this.devices.filter(dev => this.cfg.devices[dev.devId].bacnet_device === bacnetDevId);
        this.dialog.open(this.deleteBacnetDeviceDialog, {
            data: {
                bacnetDevId,
                connectedDevs: connectedDevs.map(dev => dev.devId)
            },
            maxWidth: `${BACNET_DEVICE_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteBacnetDevice(bacnetDevId, connectedDevs);
            }
        });
    }

    onClickAddDevice() {
        this.dialog.open(this.addDeviceDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank], [this.mkDeviceUniquenessValidator()])
            },
            maxWidth: `${DEVICE_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addDevice(id);
            }
        });
    }

    onClickRenameDevice(devId: string) {
        this.dialog.open(this.renameDeviceDialog, {
            data: {
                devId,
                idFormControl: new FormControl(devId, [validateNotBlank], [this.mkDeviceUniquenessValidator(devId)])
            },
            maxWidth: `${DEVICE_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== devId) {
                this.renameDevice(devId, newName);
            }
        });
    }

    onClickDeleteDevice(devId: string) {
        this.dialog.open(this.deleteDeviceDialog, {
            data: {
                devId
            },
            maxWidth: `${DEVICE_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteDevice(devId);
            }
        });
    }

    onClickDevicePollingPolicy(devId: string) {
        this.dialog.open(PollingPolicyComponent, {
            data: {
                devId,
                dev: this.cfg.devices[devId]
            } as PollingPolicyData,
            maxWidth: `${DEVICE_POLLING_POLICY_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe((policy: PollingPolicy) => {
            if (policy) {
                this.setDevicePolicy(devId, policy);
            }
        });
    }

    onClickDeviceAdvancedSettings(dev: DeviceConfig) {
        const point = this.cfg.devices[dev.devId].point;
        const points = _.isArray(point) ? point : (!!point ? [point] : []);
        const form = this.fb.group({
            expression: new FormControl(this.cfg.devices[dev.devId].expression, [
                (ctrl: AbstractControl) => this.validateExpressionAgainstPoint(ctrl, dev)
            ])
        });
        this.dialog.open(this.advancedDeviceSettingsDialog, {
            data: {
                devId: dev.devId,
                form,
                points,
                features: this.mkEditorFeatures(points)
            },
            maxWidth: `${DEVICE_ADVANCED_SETTINGS_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(keep => {
            if (keep) {
                this.setDeviceAdvancedSettings(dev,
                    form.controls.expression.value);
            }
        });
    }

    private get isValid(): boolean {
        return this.form.valid &&
            this.bacnetDevices.every(bacnetDev => bacnetDev.form.valid) &&
            this.devices.every(dev => dev.form.valid);
    }

    private updateConfig() {
        this.configEmitter.emit(this.cfg);
    }

    private updateValidity() {
        this.validEmitter.emit(this.isValid);
    }

    private mkEditorFeatures(points: string[]): Features {
        const cfg = this.appCfg.exprEditor;
        return {
            keywords: _.fromPairs([
                ...EDITOR_BUILTIN_TYPES.map(t => [t, {color: cfg.builtinTypesColor, bold: cfg.builtinsBold}]),
                ...EDITOR_BUILTIN_FUNCTIONS.map(f => [f, {color: cfg.builtinFunctionsColor, bold: cfg.builtinsBold}]),
                ...VARIABLE_ACCESSORS.map(f => [f, {
                    color: cfg.variableAccessorsColor,
                    bold: cfg.variableAccessorBold
                }]),
                ...points.map(m => [m, {color: cfg.variablesColor, bold: cfg.variablesBold}])
            ])
        };
    }

    private addBacnetDevice(bacnetDevId: string) {
        // config must be updated in order for mkBacnetDeviceConfig() to work
        this.cfg.bacnet_devices[bacnetDevId] = {
            address: null,
            id: null
        };
        this.bacnetDevices.push(this.mkBacnetDeviceConfig(bacnetDevId));
        this.bacnetDevices.sort(keySorter('bacnetDevId'));
        this.reloadBacnetDevsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameBacnetDevice(bacnetDevId: string, newName: string, connectedDevs: DeviceConfig[]) {
        const bacnetDev = this.bacnetDevices.find(dev => dev.bacnetDevId === bacnetDevId);
        bacnetDev.bacnetDevId = newName;
        this.cfg.bacnet_devices[newName] = this.cfg.bacnet_devices[bacnetDevId];
        delete this.cfg.bacnet_devices[bacnetDevId];
        this.bacnetDevices.sort(keySorter('bacnetDevId'));
        // fix connected devices
        connectedDevs.forEach(dev => {
            // NOTE: manually align form and model to avoid automatic multiple config updates
            dev.form.controls.bacnetDevId.setValue(newName, {emitEvent: false});
            this.cfg.devices[dev.devId].bacnet_device = newName;
        });
        this.reloadBacnetDevsPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteBacnetDevice(bacnetDevId: string, connectedDevs: DeviceConfig[]) {
        const idx = this.bacnetDevices.findIndex(bacnetDev => bacnetDev.bacnetDevId === bacnetDevId);
        this.bacnetDevices[idx].valueSubscription.unsubscribe();
        this.bacnetDevices[idx].statusSubscription.unsubscribe();
        this.bacnetDevices.splice(idx, 1);
        delete this.cfg.bacnet_devices[bacnetDevId];
        // fix connected devices
        connectedDevs.forEach(dev => {
            // NOTE: manually align form and model to avoid automatic multiple config updates
            dev.form.controls.bacnetDevId.setValue(null, {emitEvent: false});
            dev.form.controls.bacnetDevId.markAsTouched();
            this.cfg.devices[dev.devId].bacnet_device = null;
        });
        this.reloadBacnetDevsPages.next();
        this.updateConfig();
        // validity may change if there are some affected devices
        if (connectedDevs.length) {
            this.updateValidity();
        }
    }

    private addDevice(devId: string) {
        // config must be updated in order for mkDeviceConfig() to work
        this.cfg.devices[devId] = {
            bacnet_device: null,
            point: null,
            interval: DEFAULT_POLLING_INTERVAL
        };
        this.devices.push(this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameDevice(devId: string, newName: string) {
        const dev = this.devices.find(dev => dev.devId === devId);
        dev.devId = newName;
        this.cfg.devices[newName] = this.cfg.devices[devId];
        delete this.cfg.devices[devId];
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteDevice(devId: string) {
        const idx = this.devices.findIndex(dev => dev.devId === devId);
        this.devices[idx].valueSubscription.unsubscribe();
        this.devices[idx].statusSubscription.unsubscribe();
        this.devices.splice(idx, 1);
        delete this.cfg.devices[devId];
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity may change (e.g. in case an invalid device is deleted)
        this.updateValidity();
    }

    private setDevicePolicy(devId: string, policy: PollingPolicy) {
        this.cfg.devices[devId] = setDevicePolicy(this.cfg.devices[devId], policy);
        this.updateConfig();
        // `policy` is assumed to contain valid data, so no need for validity update
    }

    private setDeviceAdvancedSettings(dev: DeviceConfig,
                                      expression: string) {
        this.cfg.devices[dev.devId].expression = expression && expression.trim().length ? expression.trim() : null;
        this.updateConfig();
        // validity may have changed (e.g. an expression may just have been set for a multi-point device)
        dev.form.controls.point.updateValueAndValidity(); // automatically triggers a status update
    }

    private mkBacnetDeviceConfig(bacnetDevId: string): BacnetDeviceConfig {
        const cfg = {
            bacnetDevId: bacnetDevId,
            form: this.fb.group({
                address: new FormControl(this.cfg.bacnet_devices[bacnetDevId].address, [validateNotBlank]),
                id: new FormControl(this.cfg.bacnet_devices[bacnetDevId].id, [Validators.required, validateInteger, Validators.min(0)])
            })
        } as BacnetDeviceConfig;
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const bacnetDev = this.cfg.bacnet_devices[cfg.bacnetDevId];
            bacnetDev.address = value['address'];
            bacnetDev.id = value['id'];
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkDeviceConfig(devId: string): DeviceConfig {
        const cfg = {
            devId: devId,
            form: this.fb.group({
                bacnetDevId: new FormControl(this.cfg.devices[devId].bacnet_device, [Validators.required]),
                point: new FormControl(pointToForm(this.cfg.devices[devId].point), []),
                uom: new FormControl(this.cfg.devices[devId].uom, [])
            })
        } as DeviceConfig;
        // NOTE: it is crucial that `cfg` reference does not change at run-time, so that point validator is always able
        //       to access the device config structure, where the (potentially renamed) device id is stored
        cfg.form.controls.point.setValidators([
            validateNotBlankTrim([' ', ',']),
            (ctrl: AbstractControl) => this.validatePointAgainstExpression(ctrl, cfg)
        ]);
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const dev = this.cfg.devices[cfg.devId];
            dev.bacnet_device = value['bacnetDevId'] ? value['bacnetDevId'].trim() : null;
            dev.point = formToPoint(value['point']);
            dev.uom = value['uom'] && value['uom'].trim().length ? value['uom'].trim() : null;
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkDeviceUniquenessValidator(ignoreDevice?: string): AsyncValidatorFn {
        return ctrl => {
            const device = !isBlank(ctrl.value) ? ctrl.value.trim() : null;
            if (!device || (!!ignoreDevice && device === ignoreDevice)) {
                return of(null);
            }
            return this.isDeviceDuplicate(device).pipe(
                map(isDuplicate => isDuplicate ? {'duplicate': true} : null)
            );
        };
    }

    private isDeviceDuplicate(device: string): Observable<boolean> {
        // check for duplicates inside this very connector, using *current* values
        if (this.devices.find(devCfg => devCfg.devId === device)) {
            return of(true);
        }
        return this.apiService.checkConnectorDeviceExistence(device).pipe(
            switchMap(resp => resp.value ?
                this.apiService.findConnectorByDevice(device).pipe(
                    map(idResp => idResp?.id !== this.id) // ignore "this very connector" (already checked against current values)
                ) :
                of(false))
        );
    }

    private validatePointAgainstExpression(ctrl: AbstractControl, dev: DeviceConfig): ValidationErrors {
        const point = formToPoint(ctrl.value);
        if (_.isArray(point) && point.length > 1) {
            const expr = this.cfg.devices[dev.devId].expression;
            if (!expr || !expr.trim().length) {
                return {bad_expression: true};
            }
        }
        return null;
    }

    private validateExpressionAgainstPoint(ctrl: AbstractControl, dev: DeviceConfig): ValidationErrors {
        const point = this.cfg.devices[dev.devId].point;
        if (_.isArray(point) && point.length > 1) {
            if (!ctrl.value || !ctrl.value.trim().length) {
                return {bad_expression: true};
            }
        }
        return null;
    }

    private mkPagedDevices(size: number, page: number): Observable<PagedDevices> {
        let pagedDevs = [];
        for (let i = 0; i < this.devices.length; i += size) {
            pagedDevs.push(this.devices.slice(i, i + size));
        }
        return of({
            data: pagedDevs[page],
            total: this.devices.length
        } as PagedDevices);
    }

    private mkPagedBacnetDevices(size: number, page: number): Observable<PagedBacnetDevices> {
        let pagedBacnetDevs = [];
        for (let i = 0; i < this.bacnetDevices.length; i += size) {
            pagedBacnetDevs.push(this.bacnetDevices.slice(i, i + size));
        }
        return of({
            data: pagedBacnetDevs[page],
            total: this.bacnetDevices.length
        } as PagedBacnetDevices);
    }
}

function pointToForm(point: string | string[]): string {
    if (!isDefined(point)) {
        return null;
    }
    if (_.isArray(point)) {
        return point.join(', ');
    }
    return point;
}

function formToPoint(point: string): string | string[] {
    if (!point) {
        return null;
    }
    const points = point.split(',').map(p => p.trim());
    return points.length > 1 ? points : points[0];
}
